/**
 * WatchlistDropdownView — content of the "+ Add to Watchlist" Popover
 * (SE-5022).
 *
 * Renders a scrollable list of every watchlist the user is a member of, one
 * WatchlistRowView each, then an inline "+ New List" CTA at the bottom that
 * opens the canonical CreateWatchlistModal.
 *
 * SE-8306: a row the user only holds VIEWER access on is listed but rendered
 * read-only by WatchlistRowView, because the backend refuses a VIEWER's add
 * and remove alike. A control that simply vanished would read as a bug, so the
 * row says "View only" and this panel says why once, beneath the list.
 *
 * The container fires the add / remove mutation as soon as a checkbox
 * is toggled (optimistic) — this view only renders the visual state.
 * @remarks View component — no business logic.
 */

import { PlusIcon } from "lucide-react-native";
import { memo } from "react";

import WatchlistRowView, { type WatchlistDropdownItem } from "./WatchlistRow";

import { Divider } from "@/components/atoms/Divider";
import { Stack } from "@/components/atoms/Stack";
import { Icon } from "@/components/atoms/Icon";
import { Pressable } from "@/components/atoms/Pressable";
import { ScrollView } from "@/components/atoms/ScrollView";
import { Spinner } from "@/components/atoms/Spinner";
import { Text } from "@/components/atoms/Text";

export type { WatchlistDropdownItem };

/** View props for the watchlist dropdown body */
export interface WatchlistDropdownViewProps {
  /** Items to render */
  readonly items: readonly WatchlistDropdownItem[];
  /**
   * Pre-built per-row toggle handlers, keyed by watchlist id. Read-only rows
   * carry no entry. Returns a Promise so tests can await the mutation
   * lifecycle; React Native's `onPress` accepts any return value and discards
   * it.
   */
  readonly toggleHandlers: Record<string, () => Promise<void>>;
  /** Whether the initial query is still loading */
  readonly isLoading: boolean;
  /** Open the canonical CreateWatchlistModal */
  readonly onCreateList: () => void;
}

/**
 * Render the dropdown body — rows of the user's watchlists + inline create row.
 * @param props - View props
 * @param props.items - Lists to show
 * @param props.toggleHandlers - Per-row toggle handlers (editable rows only)
 * @param props.isLoading - Initial loading state
 * @param props.onCreateList - Inline + New List handler
 */
const WatchlistDropdownView = ({
  items,
  toggleHandlers,
  isLoading,
  onCreateList,
}: WatchlistDropdownViewProps) => (
  <Stack className="web:max-w-80 min-w-64 rounded-md border border-outline-subtle bg-surface-base p-2 shadow-soft-2">
    {isLoading ? (
      <Stack
        direction="horizontal"
        className="items-center justify-center px-3 py-4"
      >
        <Spinner size="small" />
      </Stack>
    ) : (
      <ScrollView className="max-h-72">
        <Stack>
          {items.length === 0 ? (
            <Text variant="body" className="px-3 py-2 text-content-muted">
              No lists yet.
            </Text>
          ) : (
            items.map(item => (
              <WatchlistRowView
                key={item.id}
                item={item}
                onToggle={toggleHandlers[item.id]}
              />
            ))
          )}
        </Stack>
      </ScrollView>
    )}

    {!isLoading && items.some(item => !item.canEdit) ? (
      <Text variant="caption" className="px-3 pt-2 text-content-muted">
        You have view-only access to some of these lists, so their players
        can&apos;t be changed here.
      </Text>
    ) : null}

    <Stack className="py-1">
      <Divider />
    </Stack>

    <Pressable
      onPress={onCreateList}
      accessibilityRole="button"
      accessibilityLabel="Create a new list"
      className="web:cursor-pointer rounded-none hover:bg-surface-raised"
    >
      <Stack direction="horizontal" className="items-center gap-3 px-3 py-2">
        <Icon as={PlusIcon} size="sm" className="text-content-primary" />
        <Text variant="unstyled" className="text-title-sm text-content-primary">
          New List
        </Text>
      </Stack>
    </Pressable>
  </Stack>
);

WatchlistDropdownView.displayName = "WatchlistDropdownView";

export default memo(WatchlistDropdownView);
